/**
 * Masked contact with an audited reveal.
 *
 * Three things this component is careful about:
 *   1. It renders the masked value until the server hands back the real one. There is no
 *      "hidden" full value in the DOM to un-hide.
 *   2. It requires a reason before it asks, because the audit row needs one.
 *   3. It re-masks after 60 seconds, so a shared screen does not keep a customer's email on
 *      it after the reason for looking has passed.
 * The step-up itself is not implemented here. A 403 step_up_required from the API opens
 * A2's dialog through the shared client and the request is retried once.
 */
import { useEffect, useRef, useState } from 'react';
import { buttonStyle } from './temp-ui';
import { useRevealContact } from './api';

export const REMASK_AFTER_MS = 60_000;

export function RevealField({
  customerKey,
  field,
  masked,
  label,
}: {
  customerKey: string;
  field: 'email' | 'phone';
  masked: string | null;
  label: string;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reveal = useRevealContact(customerKey);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  if (!masked) {
    return (
      <span style={{ color: 'var(--text-faint)' }}>
        {label}: none on file
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center' }}>
      <span data-mono="true" data-testid={`contact-${field}`}>
        {revealed ?? masked}
      </span>
      {revealed ? (
        <span className="caps-label" style={{ color: 'var(--text-faint)' }}>
          re-masks in 60s
        </span>
      ) : asking ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const reason = String(new FormData(event.currentTarget).get('reason') ?? '');
            setError(null);
            reveal.mutate(
              { field, reason },
              {
                onSuccess: (result) => {
                  setRevealed(result.value);
                  setAsking(false);
                  timer.current = setTimeout(() => setRevealed(null), REMASK_AFTER_MS);
                },
                onError: (err: unknown) => {
                  setError(err instanceof Error ? err.message : 'The reveal was refused.');
                },
              },
            );
          }}
          style={{ display: 'inline-flex', gap: 'var(--space-2)' }}
        >
          <input
            name="reason"
            required
            minLength={3}
            aria-label={`Reason for revealing ${label}`}
            placeholder="Reason"
            style={{
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-input)',
              border: 'var(--border-width) solid var(--border)',
              background: 'var(--surface-2)',
              color: 'var(--text)',
            }}
          />
          <button type="submit" style={buttonStyle('primary')} disabled={reveal.isPending}>
            {reveal.isPending ? 'Confirming' : 'Confirm'}
          </button>
        </form>
      ) : (
        <button type="button" style={buttonStyle()} onClick={() => setAsking(true)}>
          reveal
        </button>
      )}
      {error ? (
        <span role="alert" style={{ color: 'var(--crit)', fontSize: 'var(--type-meta)' }}>
          {error}
        </span>
      ) : null}
    </span>
  );
}
